import React from 'react';
import { useCartStore } from '../context/CartStore';
import { Link } from 'react-router-dom';
import { FaTrash } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const CartPage = () => {
  const items = useCartStore((state) => state.items);
  const removeItem = useCartStore((state) => state.removeItem);
  const updateQuantity = useCartStore((state) => state.updateQuantity);

  const subtotal = items.reduce((total, item) => total + item.price * item.quantity, 0);
  const tax = subtotal * 0.1;
  const deliveryCharge = items.length > 0 ? 100 : 0;
  const total = subtotal + tax + deliveryCharge;

  if (items.length === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="min-h-[80vh] bg-deepDark flex items-center justify-center px-4"
      >
        <div className="text-center p-12 max-w-lg w-full border border-white/5 bg-surfaceDark/50">
          <h2 className="text-3xl font-serif text-white mb-6 tracking-wide">Your Collection is Empty</h2>
          <p className="text-textMuted mb-10 text-sm font-light tracking-wide">Begin curating your personal selection of fine jewelry.</p>
          <div className="w-16 h-[1px] bg-gold mx-auto mb-10"></div>
          <Link to="/products" className="inline-block border border-gold text-gold px-12 py-4 text-xs font-bold tracking-widest uppercase hover:bg-gold hover:text-deepDark transition-colors duration-300">
            Explore Collection
          </Link>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen bg-deepDark py-12 px-4"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 border-b border-white/10 pb-6 flex items-baseline justify-between">
          <h1 className="text-4xl font-serif text-white tracking-wide">Acquisition Bag</h1>
          <span className="text-textMuted text-xs tracking-widest uppercase">{items.length} Piece{items.length !== 1 ? 's' : ''}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            <AnimatePresence>
              {items.map((item) => (
                <motion.div 
                  key={item._id} 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-surfaceDark border border-white/5 p-6 flex flex-col sm:flex-row gap-8 items-start sm:items-center relative group"
                >
                  <div className="w-full sm:w-32 h-40 sm:h-32 bg-[#0a0a0a] flex-shrink-0 border border-white/5 overflow-hidden">
                    <img
                      src={item.images?.[0] || '/placeholder.jpg'}
                      alt={item.name}
                      className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                    />
                  </div>
                  
                  <div className="flex-1 w-full space-y-2">
                    <h3 className="font-serif text-lg text-white tracking-wide">{item.name}</h3>
                    <p className="text-[10px] tracking-widest uppercase text-textMuted">{item.material} • {item.weight}</p>
                    {item.selectedSize && <p className="text-[10px] tracking-widest uppercase text-textMuted">Size: {item.selectedSize}</p>}
                    <p className="text-gold font-serif text-xl pt-2">₹{item.price.toLocaleString()}</p>
                  </div>

                  <div className="flex items-center gap-4 w-full sm:w-auto mt-4 sm:mt-0">
                    <div className="flex items-center border border-white/20 bg-deepDark h-10 w-28">
                      <button
                        onClick={() => updateQuantity(item._id, Math.max(1, item.quantity - 1))}
                        className="w-8 h-full text-white/50 hover:text-white transition-colors"
                      >
                        -
                      </button>
                      <input
                        type="text"
                        value={item.quantity}
                        readOnly
                        className="w-full h-full text-center bg-transparent text-white font-serif text-sm focus:outline-none"
                      />
                      <button
                        onClick={() => updateQuantity(item._id, item.quantity + 1)}
                        className="w-8 h-full text-white/50 hover:text-white transition-colors"
                      >
                        +
                      </button>
                    </div>

                    <div className="text-right w-24 hidden sm:block">
                      <p className="font-serif text-white text-lg">₹{(item.price * item.quantity).toLocaleString()}</p>
                    </div>

                    <button
                      className="text-white/20 hover:text-gold p-2 transition-colors duration-300 ml-auto sm:ml-4"
                      onClick={() => removeItem(item._id)}
                      aria-label="Remove item"
                    >
                      <FaTrash size={14} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Order Summary */}
          <div className="bg-surfaceDark border border-white/10 p-8 h-fit lg:sticky lg:top-32">
            <h2 className="text-xl font-serif text-white mb-8 tracking-wide">Summary</h2>

            <div className="space-y-4 mb-8">
              <div className="flex justify-between text-xs tracking-widest uppercase text-textMuted">
                <span>Subtotal</span>
                <span className="text-white">₹{subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs tracking-widest uppercase text-textMuted">
                <span>Taxes</span>
                <span className="text-white">₹{tax.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs tracking-widest uppercase text-textMuted">
                <span>White-Glove Delivery</span>
                <span className="text-white">₹{deliveryCharge}</span>
              </div>
              <div className="border-t border-white/10 mt-6 pt-6 flex justify-between items-baseline text-white">
                <span className="text-sm tracking-widest uppercase font-bold">Total</span>
                <span className="text-3xl font-serif text-gold">₹{total.toLocaleString()}</span>
              </div>
            </div>

            <div className="bg-gold/5 border border-gold/20 p-4 mb-8">
              <p className="text-[10px] tracking-widest uppercase text-gold leading-relaxed">
                ✦ Your acquisition requires signature upon delivery. Fully insured logistics exclusively via secure transport.
              </p>
            </div>

            <Link
              to="/checkout"
              className="block w-full text-center bg-gold text-deepDark py-4 text-xs font-bold tracking-widest uppercase hover:bg-white transition-colors duration-300"
            >
              Secure Checkout
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CartPage;
